import { useId, type ReactNode } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { useFormatters } from '@shared/lib/i18n/formatters';
import { Icon } from './Icon';
import { Skeleton } from './Feedback';

export interface StatTileProps {
  label: ReactNode;
  value: ReactNode;
  /** Change vs the comparison period; sign decides colour and arrow. */
  delta?: { value: number; label: ReactNode } | undefined;
  footer?: ReactNode;
  loading?: boolean;
  className?: string;
}

/** Label, big tabular value, coloured delta only (design system §4.24). */
export function StatTile({ label, value, delta, footer, loading, className }: StatTileProps) {
  const f = useFormatters();
  const positive = delta ? delta.value >= 0 : false;
  return (
    <div className={cn('flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface p-6', className)}>
      <span className="text-sm font-medium text-secondary">{label}</span>
      {loading ? <Skeleton className="h-8 w-24" /> : <span className="tabular text-3xl font-semibold text-primary">{value}</span>}
      {delta ? (
        <span className={cn('flex items-center gap-1 text-xs', positive ? 'text-success-text' : 'text-danger-text')}>
          <Icon icon={positive ? ArrowUp : ArrowDown} size={14} mirror={false} />
          <bdi className="tabular">{f.number(Math.abs(delta.value))}</bdi>
          <span className="text-muted">{delta.label}</span>
        </span>
      ) : null}
      {footer ? <div className="text-sm">{footer}</div> : null}
    </div>
  );
}

export interface ProgressBarProps {
  value: number;
  max?: number;
  label: ReactNode;
  /** Visible value text, e.g. "18 of 30 juz · 60 %"; also used as aria-valuetext. */
  valueText: string;
  compact?: boolean;
  className?: string;
}

/** Fill grows from the start side (flips in RTL). Always shows a text value (design system §4.23). */
export function ProgressBar({ value, max = 100, label, valueText, compact, className }: ProgressBarProps) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const labelId = `pb${useId().replace(/:/g, '')}`;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span id={labelId} className="font-medium text-secondary">
          {label}
        </span>
        <bdi className="tabular text-muted">{valueText}</bdi>
      </div>
      <div
        role="progressbar"
        aria-labelledby={labelId}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueText}
        className={cn('w-full overflow-hidden rounded-full bg-surface-sunken', compact ? 'h-1' : 'h-2')}
      >
        <div
          className="h-full rounded-full bg-primary-chart transition-[inline-size] duration-progress ease-out"
          style={{ inlineSize: `${pct}%` }}
        />
      </div>
    </div>
  );
}
